import { useMutation } from "@apollo/client/react"; 
import React, { useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import SAVE_VISITA_VALORACION_AP, { adaptAddVisitaPresencialAPValoracionInput } from "../../../../../../data/graphQL/mutations/Visitas/saveValoracionAP";
import { useVisita } from "../../../../../../context/VisitaContext";

import { useStepSave } from "../../../../hooks/useStepSave";
import { VALORACION_ACCESO_INTEGRO_ID, VALORACION_ACCESO_PERMEABLE_ID } from "../../../../constants/valoracionAccesoIds";
import { validateStepFields } from "../../../../utils/visitaStepValidation";

export default function ValoracionAP({ esUrgencia, readOnly, onSaveRef, onExposeFormatted }) {
  const [formData, setFormData] = useState({});

  const { visita, setVisita } = useVisita();

  //QUERY y MUTATION
  const [save] = useMutation(SAVE_VISITA_VALORACION_AP);

  const isEstadoHidratacionOptimo = formData.estadoHidratacionOptimo;
  const controlGlucemia = formData.controlGlucemia;

  const formattedInputs = useMemo(() => {
    return inputs.map((input) => {
      const newInput = { ...input };

      switch (input.name) {
        case "obsHidratacionNo":
          newInput.hidden = isEstadoHidratacionOptimo;
          break;
        case "valorGlucemia":
          newInput.hidden = !controlGlucemia;
          break;
      }

      if (esUrgencia) newInput.required = false;

      if (readOnly) newInput.disabled = true;

      return newInput;
    });
  }, [readOnly, esUrgencia, isEstadoHidratacionOptimo, controlGlucemia]);


  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'estadoHidratacionOptimo' && !value)
      handleChange('obsHidratacionNo', '');

    if (name === 'controlGlucemia' && !value)
      handleChange('valorGlucemia', null);
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });

    const { data } = await save({ variables: { input: adaptAddVisitaPresencialAPValoracionInput(formData) } });

    const idPresencialAP = data?.saveVisitaPresencialAPValoracion?.idPresencialAP;
    if (!idPresencialAP) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      integro: formData.accesoIntegro,
      permeable: formData.accesoPermeable,
      idPresencialAP: idPresencialAP
    }));

  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'valoracionAP',
      stepName: 'Valoracion AP',
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: input.halfSpace ? 4 : 12 }} key={input.name}>
              { input.type !== 'label' && <RenderInputByTypeMobile
                key={`${input.name}-${input.type}${input.type === 'multiple_boolean' ? formData[input.name] : ''}`}
                label={input.displayName}
                value={formData[input.name]}
                handleChange={handleChange}
                input={input} />}
              {(input.type === 'label') &&
                <label style={{ textDecoration: 'underline', fontSize: '1rem' }}>{input.displayName}</label>
              }
            </Grid>
          )}
      </Grid>
    </CardBox>);

}
const inputs = [
  {
    name: "observacionSitioPuncion",
    displayName: "Observación del sitio de punción",
    type: "long_string",
    maxLength: 250,
    required: true
  },
  {
    name: "acceso",
    displayName: "Acceso",
    type: "label",
  },
  {
    name: "accesoIntegro",
    displayName: "Integro",
    type: "boolean",
    lightTitle: true,
    halfSpace: true,
    required: true
  },
  {
    name: "accesoPermeable",
    displayName: "Permeable",
    type: "boolean",
    lightTitle: true,
    halfSpace: true,
    required: true
  },
  {
    name: "presenciaEdemas",
    displayName: "Presencia de edemas",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "diuresis",
    displayName: "Diuresis",
    type: "multiple_boolean",
    lightTitle: true,
    required: true,
    options: [
      { value: true, label: "Positiva" },
      { value: false, label: "Negativa" }]

  },
  {
    name: "cantidad",
    displayName: "Cantidad (ml)",
    type: "int",
    onlyWhen: { field: "diuresis", value: true }
  },
  {
    name: "estadoHidratacionOptimo",
    displayName: "Estado de hidratación (Óptimo)",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "obsHidratacionNo",
    displayName: "Obs. Si es NO",
    type: "long_string",
    maxLength: 4000,
    required: true,
    onlyWhen: { field: "estadoHidratacion", value: false }
  },
  {
    name: "controlGlucemia",
    displayName: "Control de glucemia",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "valorGlucemia",
    displayName: "Valor",
    type: "int",
    required: true,
    onlyWhen: { field: "controlGlucemia", value: true }
  }];